import { HttpClient } from '@angular/common/http';
import { Inject } from '@angular/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { Observable, of } from 'rxjs';
import urlJoin from 'url-join';
import { fwHttpClientUtils } from '../models';
import { BaseHttpService } from './base-http.service';
import { tap } from 'rxjs/operators';

/**
 * BaseHttpReqRespService is an abstract class that extends BaseHttpService.
 * It provides methods for parsing Request, Response parameters to api's
 * making HTTP requests with Okta authentication.
 */
export abstract class BaseHttpReqRespService extends BaseHttpService {

  /**
   * Constructor for the BaseHttpReqRespService.
   * @param {HttpClient} http - The HttpClient to be used for making HTTP requests.
   * @param {OktaAuth} auth - The OktaAuth instance for getting access tokens.
   */
  constructor(http: HttpClient
             , @Inject(OKTA_AUTH) protected auth: OktaAuth) {
    super(http, auth);
  }

  /**
   * Flag to enable logging of the time taken for requests.
   */
  public enableConsoleLogTiming : boolean = true;

  /**
   * Method to make a GET request.
   * @param {Req} req - The request parameters.
   * @param {string} endpoint - The endpoint to make the request to.
   * @returns {Observable<Resp>} - An Observable of the response.
   */
  getData$<Req, Resp>(req: Req, endpoint: string, logTime: boolean | null = null): Observable<Resp> {

    let emptyResp: Resp = {} as Resp;
    let token = this.getToken();

    // Capture start time if logging is enabled
    const startTime = (logTime ?? this.enableConsoleLogTiming) ? new Date().getTime() : null;

    if (token) {
      let url = urlJoin(this.baseUrl, endpoint);
      const httpOptions = {
        headers: this.getHttpBearerHeaders(token),
        params: fwHttpClientUtils.toHttpParams(req)
      };
      return this.http.get<Resp>(url, httpOptions).pipe(
        tap(() => {
          if (startTime !== null) {
            const endTime = new Date().getTime();
            const duration = endTime - startTime;
          }
        })
      );;
    }
    else {
      return of(emptyResp);
    }
  }

  /**
   * Method to make a POST request.
   * @param {Req} req - The request body.
   * @param {string} endpoint - The endpoint to make the request to.
   * @returns {Observable<Resp>} - An Observable of the response.
   */
  setData$<Req, Resp>(req: Req, endpoint: string, logTime: boolean | null = null): Observable<Resp> {

    let emptyResp: Resp = {} as Resp;
    let token = this.getToken();

    // Capture start time if logging is enabled
    const startTime = (logTime ?? this.enableConsoleLogTiming) ? new Date().getTime() : null;

    if (token) {
      let url = urlJoin(this.baseUrl, endpoint);
      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
      };
      return this.http.post<Resp>(url, req, httpOptions).pipe(
        tap(() => {
          if (startTime !== null) {
            const endTime = new Date().getTime();
            const duration = endTime - startTime;
          }
        })
      );
    }
    else {
      return of(emptyResp);
    }
  }

  /**
   * Method to make a POST request.
   * @param {Req} req - The request body.
   * @param {string} endpoint - The endpoint to make the request to.
   * @param {boolean} logTime - Flag to log the time taken for the request.
   * @returns {Observable<Resp>} - An Observable of the response.
   */
  putData$<Req, Resp>(req: Req, endpoint: string, logTime: boolean | null = null): Observable<Resp> {

    let emptyResp: Resp = {} as Resp;
    let token = this.getToken();

    if (token) {
      let url = urlJoin(this.baseUrl, endpoint);
      const httpOptions = {
        headers: this.getHttpBearerHeaders(token)
      };

      // Capture start time if logging is enabled
      const startTime = (logTime ?? this.enableConsoleLogTiming) ? new Date().getTime() : null;

      return this.http.put<Resp>(url, req, httpOptions).pipe(
        tap(() => {
          if (startTime !== null) {
            const endTime = new Date().getTime();
            const duration = endTime - startTime;
          }
        })
      );
    }
    else {
      return of(emptyResp);
    }
  }

  /**
   * Method to make a DELETE request.
   * @param {Req} req - The request parameters.
   * @param {string} endpoint - The endpoint to make the request to.
   * @returns {Observable<Resp>} - An Observable of the response.
   */
  deleteData$<Req, Resp>(req: Req, endpoint: string, logTime: boolean | null = null): Observable<Resp> {

    let emptyResp: Resp = {} as Resp;
    let token = this.getToken();

    // Capture start time if logging is enabled
    const startTime = (logTime ?? this.enableConsoleLogTiming) ? new Date().getTime() : null;

    if (token) {
      let url = urlJoin(this.baseUrl, endpoint);
      const httpOptions = {
        headers: this.getHttpBearerHeaders(token),
        params: fwHttpClientUtils.toHttpParams(req)
      };
      return this.http.delete<Resp>(url, httpOptions).pipe(
        tap(() => {
          if (startTime !== null) {
            const endTime = new Date().getTime();
            const duration = endTime - startTime;
          }
        })
      );
    }
    else {
      return of(emptyResp);
    }
  }

  
}

